<!-- Sits in the page's side panel, which names it and frames it; this is its contents only. -->
<section class="flex flex-col gap-1.5">
  <header class="flex flex-wrap items-center gap-x-2 gap-y-1">
    <guarantees-chip tone="neutral">{{ visible().length }}</guarantees-chip>

    @if (attentionCount() > 0) {
      <guarantees-chip
        tone="warning"
        icon="hourglass_bottom"
        [tooltip]="'guaranteeV2.message.casesNeedAttentionHint' | translate: { count: attentionCount() }"
        >{{ attentionCount() }}</guarantees-chip
      >
    }

    @if (loading()) {
      <guarantees-spinner class="text-neutral-400"></guarantees-spinner>
    }

    <span class="ml-auto flex flex-wrap items-center gap-1.5">
      @if (closedCount() > 0) {
        <button
          [class]="buttons.ghost.sm"
          type="button"
          [matTooltip]="'guaranteeV2.message.showClosedCasesHint' | translate"
          (click)="toggleClosed()"
        >
          <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">{{ showClosed() ? "visibility_off" : "visibility" }}</mat-icon>
          {{ (showClosed() ? "guaranteeV2.action.hideClosedCases" : "guaranteeV2.action.showClosedCases") | translate: { count: closedCount() } }}
        </button>
      }

      @if (!readOnly()) {
        <button
          type="button"
          [class]="buttons.primary.sm"
          [disabled]="saving()"
          [matTooltip]="'guaranteeV2.message.createCaseHint' | translate"
          (click)="create()"
        >
          <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">create_new_folder</mat-icon>
          {{ "guaranteeV2.action.createCase" | translate }}
        </button>
      }
    </span>
  </header>

  @if (error(); as failure) {
    <p class="text-sm text-error-600">{{ failure | translate }}</p>
  } @else if (visible().length === 0 && !loading()) {
    <p class="text-sm text-neutral-400">{{ "guaranteeV2.message.noCases" | translate }}</p>
  }

  <ul class="flex flex-col gap-1">
    @for (view of visible(); track view.case.id) {
      @let expanded = isExpanded(view.case.id);
      <!-- Addressed by id so a case opened from the stream can be brought into view here. -->
      <li
        class="rounded-lg border transition-colors duration-200 motion-reduce:transition-none"
        [class]="focusCaseId() === view.case.id ? 'border-primary-300 bg-primary-50/40' : 'border-neutral-200'"
        [attr.data-case-id]="view.case.id"
      >
        <div class="flex flex-wrap items-center gap-x-2 gap-y-1 px-2 py-1.5">
          <button
            class="flex min-w-0 flex-1 flex-col gap-0.5 text-left focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
            type="button"
            [attr.aria-expanded]="expanded"
            (click)="toggle(view.case.id)"
          >
            <span class="flex w-full flex-wrap items-center gap-x-2 gap-y-1">
              <guarantees-chip
                [tone]="tone(view.status)"
                [icon]="icon(view.status)"
                [tooltip]="'guaranteeV2.message.caseStatus' + view.status | translate"
                >{{ "guaranteeV2.label.caseStatus" + view.status | translate }}</guarantees-chip
              >

              <span class="text-sm font-semibold text-neutral-700">{{ view.case.code || ("guaranteeV2.label.caseWithoutName" | translate) }}</span>

              @if (view.codes.length > 0) {
                <span
                  class="truncate rounded bg-neutral-100 px-1 font-mono text-sm font-semibold text-neutral-600"
                  [matTooltip]="'guaranteeV2.message.caseDiagnosesHint' | translate"
                  >{{ view.codes.join(", ") }}</span
                >
              }

              @if (view.guarantees.length > 0) {
                <guarantees-chip
                  tone="neutral"
                  icon="event"
                  [tooltip]="'guaranteeV2.message.caseGuaranteeCountHint' | translate: { count: view.guarantees.length }"
                  >{{ view.guarantees.length }}</guarantees-chip
                >
              }

              <mat-icon class="ml-auto h-4! w-4! shrink-0 text-base! leading-4! text-neutral-300">{{ expanded ? "expand_less" : "expand_more" }}</mat-icon>
            </span>

            <!--
              The span, on the collapsed row rather than inside it: which care a case admits is the
              first question asked of one, and a list that hides it makes every row look alike.
            -->
            <span class="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-neutral-400">
              <span
                class="tabular-nums"
                [matTooltip]="'guaranteeV2.message.casePeriodRangeHint' | translate"
              >
                {{ view.case.startedAt | date: "dd.MM.yy" }} &ndash;
                {{ view.case.expiresAt ? (view.case.expiresAt | date: "dd.MM.yy") : ("guaranteeV2.label.caseWithoutExpiry" | translate) }}
              </span>

              @if (view.daysToExpiry !== undefined) {
                <span
                  class="font-medium tabular-nums"
                  [class]="expiryClass(view.status)"
                >
                  {{
                    (view.daysToExpiry < 0 ? "guaranteeV2.message.caseExpiredDaysAgo" : "guaranteeV2.message.caseExpiresInDays")
                      | translate: { days: view.daysToExpiry < 0 ? -view.daysToExpiry : view.daysToExpiry }
                  }}
                </span>
              }
            </span>

            <!-- Why the case was opened, in the words of whoever opened it. Kept on the closed row. -->
            @if (view.firstComment; as comment) {
              <span class="flex min-w-0 items-center gap-1 text-sm text-neutral-500">
                <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-300">chat_bubble_outline</mat-icon>
                <span class="min-w-0 truncate">{{ comment.text }}</span>
              </span>
            }
          </button>

          @if (!readOnly()) {
            <button
              class="shrink-0 self-start"
              type="button"
              [class]="menuButtonClass"
              [disabled]="saving()"
              [matMenuTriggerFor]="caseMenu"
              [attr.aria-label]="'guaranteeV2.action.caseActions' | translate"
              [matTooltip]="'guaranteeV2.action.caseActions' | translate"
            >
              <mat-icon class="h-4! w-4! text-base! leading-4!">more_vert</mat-icon>
            </button>

            <!-- Everything that changes the case itself, in one place. The row below only reads it. -->
            <mat-menu
              #caseMenu="matMenu"
              [class]="menu.panel"
            >
              <ng-template matMenuContent>
                @if (!view.case.isDeleted) {
                  <button
                    mat-menu-item
                    [class]="menu.item"
                    type="button"
                    (click)="edit(view)"
                  >
                    <mat-icon [class]="menu.icon + ' text-primary-600'">edit</mat-icon>
                    <span [class]="menu.label">{{ "guaranteeV2.action.editCase" | translate }}</span>
                  </button>
                }
                <!-- Closing and undoing it, in the one slot: a case is only ever one of the two. -->
                @if (view.status === statuses.Closed) {
                  <button
                    mat-menu-item
                    [class]="menu.item"
                    type="button"
                    (click)="reopen(view)"
                  >
                    <mat-icon [class]="menu.icon + ' text-primary-600'">restart_alt</mat-icon>
                    <span [class]="menu.label">
                      {{ "guaranteeV2.action.reopenCase" | translate }}
                      @if (reopensExpired(view)) {
                        <span [class]="menu.hint">{{ "guaranteeV2.message.reopenExpiredHint" | translate }}</span>
                      }
                    </span>
                  </button>
                } @else if (view.status !== statuses.Deleted) {
                  <button
                    mat-menu-item
                    [class]="menu.item"
                    type="button"
                    (click)="close(view)"
                  >
                    <mat-icon [class]="menu.icon">check_circle</mat-icon>
                    <span [class]="menu.label">{{ "guaranteeV2.action.closeCase" | translate }}</span>
                  </button>
                }
                <!--
                  Unreachable as things stand: the list read filters soft-deleted cases out on the
                  server, so no deleted case ever arrives here to be restored. Kept because the act
                  itself works — the moment that read answers with them, this is what undoes one.
                -->
                @if (view.case.isDeleted) {
                  <button
                    mat-menu-item
                    [class]="menu.item"
                    type="button"
                    (click)="restore(view)"
                  >
                    <mat-icon [class]="menu.icon">restore_from_trash</mat-icon>
                    <span [class]="menu.label">{{ "guaranteeV2.action.restoreCase" | translate }}</span>
                  </button>
                } @else {
                  @let filed = view.guarantees.length;
                  <button
                    mat-menu-item
                    [class]="menu.item"
                    type="button"
                    [disabled]="filed > 0"
                    (click)="remove(view)"
                  >
                    <mat-icon [class]="menu.icon + (filed > 0 ? '' : ' text-error-600')">delete</mat-icon>
                    <span [class]="menu.label">
                      {{ "guaranteeV2.action.deleteCase" | translate }}
                      <!--
                        Says why rather than only greying out: the way out is to unfile them. The
                        count is a floor — it sees the loaded window — so this blocks on what is
                        proven and the confirmation carries the doubt for the rest.
                      -->
                      @if (filed > 0) {
                        <span [class]="menu.hint">{{ "guaranteeV2.message.caseDeleteBlocked" | translate: { count: filed } }}</span>
                      }
                    </span>
                  </button>
                }
              </ng-template>
            </mat-menu>
          }
        </div>

        <!-- The case as it stands. Nothing here amends it; that is the menu above and its form. -->
        @if (expanded) {
          <div
            class="flex flex-col gap-3 border-t border-neutral-200 px-2 py-2 transition duration-200 ease-out motion-reduce:transition-none starting:-translate-y-1 starting:opacity-0"
          >
            @if (view.case.isDeleted) {
              <guarantees-callout
                tone="error"
                stack
              >
                <span class="inline-flex items-center gap-1 text-sm font-semibold text-error-700">
                  <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">delete</mat-icon>
                  {{ "guaranteeV2.label.deleted" | translate }}
                  @if (view.case.deletedAt; as deletedAt) {
                    <span class="font-normal tabular-nums">{{ deletedAt | date: "dd.MM.yy HH:mm" }}</span>
                  }
                  @if (view.case.deletedByName; as name) {
                    <span class="font-normal">&middot; {{ name }}</span>
                  }
                </span>
                @if (view.case.deletionReason; as reason) {
                  <span class="text-sm text-error-700">{{ reason }}</span>
                }
              </guarantees-callout>
            }

            <guarantees-panel-section [label]="'guaranteeV2.label.case' | translate">
              <div class="grid grid-cols-[repeat(auto-fill,minmax(8rem,1fr))] gap-x-4 gap-y-2">
                <guarantees-fact
                  [label]="'guaranteeV2.label.caseStartsAt' | translate"
                  [hint]="'guaranteeV2.message.caseStartsAtHint' | translate"
                  >{{ view.case.startedAt | date: "dd.MM.yy" }}</guarantees-fact
                >
                <guarantees-fact
                  [label]="'guaranteeV2.label.caseExpiresAt' | translate"
                  [hint]="'guaranteeV2.message.caseExpirySetHint' | translate"
                  >{{ view.case.expiresAt ? (view.case.expiresAt | date: "dd.MM.yy") : "—" }}</guarantees-fact
                >
                @if (view.case.endedAt; as endedAt) {
                  <guarantees-fact [label]="'guaranteeV2.label.caseEndedAt' | translate">{{ endedAt | date: "dd.MM.yy" }}</guarantees-fact>
                }
                <guarantees-fact [label]="'guaranteeV2.label.createdBy' | translate">{{ view.case.createdByName || "—" }}</guarantees-fact>
                @if (view.case.updatedByName; as updatedBy) {
                  <guarantees-fact [label]="'guaranteeV2.label.updatedBy' | translate">{{ updatedBy }}</guarantees-fact>
                }
              </div>
            </guarantees-panel-section>

            <!-- One set, curated in the form. Case search ranks on it; nothing else grows it. -->
            <guarantees-panel-section
              [label]="'guaranteeV2.label.caseDiagnoses' | translate"
              [hint]="'guaranteeV2.message.caseDiagnosesHint' | translate"
            >
              @let caseCodings = codingsOf(view);
              @if (caseCodings.length > 0) {
                <div class="flex flex-wrap gap-1">
                  @for (coding of caseCodings; track coding.code) {
                    <guarantees-coding-chip
                      readOnly
                      [coding]="coding"
                    ></guarantees-coding-chip>
                  }
                </div>
              } @else {
                <span class="text-sm text-neutral-400">{{ "guaranteeV2.message.caseWithoutDiagnoses" | translate }}</span>
              }
            </guarantees-panel-section>

            <!--
              What the case holds within the loaded window, each guarantee with the diagnoses that
              filed it here. The window is why the label names a period: the record can hold older
              guarantees this list has never seen, so an empty one means nothing was loaded, not
              that nothing was filed.
            -->
            <guarantees-panel-section
              [label]="'guaranteeV2.label.caseGuarantees' | translate"
              [hint]="'guaranteeV2.message.caseGuaranteesHint' | translate"
            >
              @if (!readOnly()) {
                <button
                  [class]="buttons.soft.sm + ' self-start'"
                  type="button"
                  [disabled]="saving()"
                  [matMenuTriggerFor]="assignMenu"
                  [matTooltip]="'guaranteeV2.message.addGuaranteeToCaseHint' | translate"
                >
                  <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">add</mat-icon>
                  {{ "guaranteeV2.action.addGuaranteeToCase" | translate }}
                </button>

                <mat-menu
                  #assignMenu="matMenu"
                  [class]="menu.panel"
                >
                  <ng-template matMenuContent>
                    @for (entry of candidates(view); track entry.guaranteeId + entry.diagnosis.id) {
                      <button
                        mat-menu-item
                        [class]="menu.item"
                        type="button"
                        (click)="assignDiagnosis(view, entry)"
                      >
                        <mat-icon [class]="menu.icon + ' ' + (entry.diagnosis.caseId ? 'text-warning-500' : 'text-neutral-300')">{{
                          entry.diagnosis.caseId ? "folder_open" : "folder_off"
                        }}</mat-icon>
                        <span [class]="menu.label">
                          {{ diagnosisLabel(entry) }}
                          <span [class]="menu.hint">{{ entry.visitDate | date: "dd.MM.yy" }}</span>
                        </span>
                      </button>
                    } @empty {
                      <!-- The span is why: a guarantee outside it is not offered here at all. -->
                      <span [class]="menu.empty">{{ "guaranteeV2.message.noAssignableInCasePeriod" | translate }}</span>
                    }
                  </ng-template>
                </mat-menu>
              }

              @for (entry of view.guarantees; track entry.guaranteeId) {
                <div class="flex flex-col gap-0.5 rounded-md px-1.5 py-1 hover:bg-neutral-50">
                  <!-- Named by what pressing it does: its own text is a date and a partner, which is not an act. -->
                  <button
                    class="flex min-w-0 items-center gap-2 text-left text-sm focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
                    type="button"
                    [attr.aria-label]="('guaranteeV2.message.openCaseGuaranteeHint' | translate) + ' — ' + (entry.visitDate | date: 'dd.MM.yy')"
                    [matTooltip]="'guaranteeV2.message.openCaseGuaranteeHint' | translate"
                    (click)="guaranteeSelected.emit(entry.guaranteeId)"
                  >
                    <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-primary-500">north_east</mat-icon>
                    <span class="shrink-0 font-semibold text-neutral-600 tabular-nums">{{ entry.visitDate | date: "dd.MM.yy" }}</span>
                    <span class="min-w-0 truncate text-neutral-500">{{ entry.partnerName || ("guaranteeV2.label.withoutPartner" | translate) }}</span>
                  </button>

                  @for (filed of entry.diagnoses; track filed.diagnosisId) {
                    <span class="flex min-w-0 items-center gap-1 pl-5 text-sm">
                      <span class="shrink-0 font-mono font-semibold text-neutral-600">{{ filed.coding?.code }}</span>
                      <span class="min-w-0 truncate text-neutral-500">{{ filed.coding?.name }}</span>

                      @if (!readOnly()) {
                        <button
                          class="ml-auto shrink-0"
                          type="button"
                          [class]="unlinkButtonClass"
                          [disabled]="saving()"
                          [attr.aria-label]="('guaranteeV2.action.unassignCase' | translate) + ' — ' + codingLabel(filed.coding)"
                          [matTooltip]="'guaranteeV2.action.unassignCase' | translate"
                          (click)="unassignDiagnosis(view, filed.guaranteeId, filed.diagnosisId)"
                        >
                          <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">link_off</mat-icon>
                        </button>
                      }
                    </span>
                  }
                </div>
              } @empty {
                <span class="text-sm text-neutral-400">{{ "guaranteeV2.message.caseWithoutGuarantees" | translate }}</span>
              }
            </guarantees-panel-section>

            <guarantees-panel-section [label]="'guaranteeV2.label.caseComments' | translate">
              @for (comment of view.case.comments ?? []; track comment.createdAt + comment.text) {
                <p class="rounded-md bg-neutral-50 px-1.5 py-1 text-sm text-neutral-600">
                  <span class="font-medium text-neutral-700">{{ comment.authorName }}</span>
                  <span class="ml-1 text-neutral-400 tabular-nums">{{ comment.createdAt | date: "dd.MM.yy" }}</span>
                  <span class="mt-0.5 block">{{ comment.text }}</span>
                </p>
              } @empty {
                <span class="text-sm text-neutral-400">{{ "guaranteeV2.message.caseWithoutComments" | translate }}</span>
              }

              <!-- A comment adds to the record rather than amending it, so it is asked for here. -->
              @if (!readOnly()) {
                @let comment = commentDraft(view.case.id);
                <form
                  class="flex flex-wrap items-center gap-1.5"
                  (ngSubmit)="submitComment(view)"
                >
                  <input
                    guaranteesControl
                    class="min-w-40 flex-1"
                    name="comment"
                    type="text"
                    autocomplete="off"
                    [placeholder]="'guaranteeV2.label.addCaseComment' | translate"
                    [ngModel]="comment"
                    (ngModelChange)="setCommentDraft(view.case.id, $event)"
                  />
                  <button
                    [class]="buttons.soft.sm"
                    type="submit"
                    [disabled]="saving() || !comment.trim()"
                  >
                    <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">add_comment</mat-icon>
                    {{ "guaranteeV2.action.addComment" | translate }}
                  </button>
                </form>
              }
            </guarantees-panel-section>
          </div>
        }
      </li>
    }
  </ul>
</section>
